import React, { useState } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import { Table, Button, Badge, Accordion } from 'react-bootstrap';
import 'react-toastify/dist/ReactToastify.css';
import { get } from '../../services/apiController';
import Item from '../../components/item';
import saveAs from 'file-saver';
import moment from 'moment';

const VendorPriceReport = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [state, setState] = useState({
    itemId: '',
  });

  const getPriceHistory = (downloadExcel) => {
    if (!state.itemId) {
      toast.error('Please select an item');
      return;
    }

    setLoading(true);
    let url = `/inv/report/vendor-price-report/?itemId=${state.itemId}`;

    if(downloadExcel){
      url += `&downloadExcel=true`;
    }

    get(url, { responseType: downloadExcel ? 'arraybuffer' : 'json' })

      .then((res) => {
        if (downloadExcel) {
          const fileName = `Vendor Price Report - ${state.itemId}.xlsx`;
          const blob = new Blob([res], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
          saveAs(blob, fileName);
        } else {
          setData(res?.data);
        }
      })
      .catch((err) => {
        toast.error(err?.response?.data?.data?.message || err?.message);
        setData(null);
      })
      .finally(() => {
        setLoading(false);
      });
  };
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const calculatePriceChange = (currentRate, previousRate) => {
    if (!previousRate) return null;
    const change = ((currentRate - previousRate) / previousRate) * 100;
    return change.toFixed(2);
  };

  return (
    <>
      <ToastContainer />
      <div className="card">
        <div className="card-header white-color" style={{ padding: '1rem 1.7rem' }}>
          <div className="col-6">
            <div className="d-flex" style={{ height: '100%', alignItems: 'center' }}>
              <h4 className="card-title" style={{ fontSize: '16px' }}>
                Vendor Price Report
                {data && (
                  <span className="ms-2">
                    | <strong>{data.item?.name}</strong>
                    {data.item?.code && <small className="text-muted"> ({data.item.code})</small>}
                  </span>
                )}
              </h4>
            </div>
          </div>
          <div className="card-header-actions col-6 d-flex justify-content-end">
            <div style={{ maxWidth: '250px', marginRight: '20px' }}>
              <Item
                itemId={state.itemId}
                name="itemId"
                onChange={(itemId) => {
                  setState({ ...state, itemId });
                  setData(null);
                }}
              />
            </div>
            <div className="d-flex" style={{ maxWidth: '300px', flexDirection: 'row', alignItems: 'center', gap: '10px' }}>
              <Button
                onClick={() => getPriceHistory(false)}
                type="submit"
                className="btn app-btn-default dark-color"
                disabled={loading || !state.itemId}
              >
                {loading ? 'Loading...' : 'Get Report'}
              </Button>
              {data && (
                <Button
                  onClick={() => getPriceHistory(true)}
                  type="button"
                  className="btn btn-success"
                  disabled={loading || !state.itemId}
                >
                  {loading ? 'Downloading...' : 'Export Excel'}
                </Button>
              )}
            </div>
          </div>
        </div>

        <div className="card-body px-0 vehicle-requests-table pt-0 pb-0">
          {!data && !loading && (
            <div className="text-center p-4">
              <p className="text-muted">Select an item and click "Get Report" to view price history</p>
            </div>
          )}

          {loading && (
            <div className="text-center p-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          )}

          {data && (
            <>
              {/* Summary Cards */}
              <div className="row px-3 py-2">
                <div className="col-md-3">
                  <div className="card bg-light">
                    <div className="card-body text-center p-2">
                      <h6 className="card-title mb-1">Total Vendors</h6>
                      <h4 className="text-primary mb-0">{data.summary?.totalVendors || 0}</h4>
                    </div>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="card bg-light">
                    <div className="card-body text-center p-2">
                      <h6 className="card-title mb-1">Price Changes</h6>
                      <h4 className="text-info mb-0">{data.summary?.totalPriceChanges || 0}</h4>
                    </div>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="card bg-light">
                    <div className="card-body text-center p-2">
                      <h6 className="card-title mb-1">Total GRNs</h6>
                      <h4 className="text-success mb-0">{data.summary?.totalGrnRecords || 0}</h4>
                    </div>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="card bg-light">
                    <div className="card-body text-center p-2">
                      <h6 className="card-title mb-1">Best Current Rate</h6>
                      <h4 className="text-warning mb-0">
                        {data.summary?.lowestCurrentRate ? formatCurrency(data.summary.lowestCurrentRate) : 'N/A'}
                      </h4>
                    </div>
                  </div>
                </div>
              </div>

              {/* Current Rates Table */}
              <div className="px-3 py-2">
                <h5 className="mb-3">Current Rates Per Vendor</h5>
                <div className="row">
                  <div className="col-md-8">
                    <Table responsive striped bordered size="sm">
                      <thead className="table-dark">
                        <tr>
                          <th>Rank</th>
                          <th>Vendor Name</th>
                          <th>Current Rate</th>
                          <th>Last Updated</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.vendors.map((vendor, index) => (
                          <tr key={vendor.vendorId}>
                            <td>
                              <Badge bg={index === 0 ? 'success' : index === 1 ? 'warning' : 'secondary'}>
                                #{index + 1}
                              </Badge>
                            </td>
                            <td>{vendor.vendorName}</td>
                            <td>
                              <strong className={index === 0 ? 'text-success' : ''}>
                                {formatCurrency(vendor.currentRate)}
                              </strong>
                            </td>
                            <td>{moment(vendor.lastUpdated).format('DD-MMM-YYYY')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </div>
              </div>

              {/* Vendor Groups */}
              {data.vendors?.length > 0 ? (
                <div className="px-3 py-2">
                  <Accordion defaultActiveKey="0">
                    {data.vendors.map((vendor, index) => (
                      <Accordion.Item key={vendor.vendorId} eventKey={index.toString()}>
                        <Accordion.Header>
                          <div className="d-flex justify-content-between align-items-center w-100 me-3">
                            <div>
                              <Badge bg={index === 0 ? 'success' : index === 1 ? 'warning' : 'secondary'} className="me-2">
                                #{index + 1}
                              </Badge>
                              <strong>{vendor.vendorName}</strong>
                            </div>
                            <div className="text-end">
                              <span className="me-3">Current Rate: <strong>{formatCurrency(vendor.currentRate)}</strong></span>
                              <Badge bg="info">{vendor.totalGrns} GRN{vendor.totalGrns > 1 ? 's' : ''}</Badge>
                            </div>
                          </div>
                        </Accordion.Header>
                        <Accordion.Body>
                          <Table responsive striped bordered size="sm">
                            <thead className="table-dark">
                              <tr>
                                <th>GRN Date</th>
                                <th>Invoice Date</th>
                                <th>Rate</th>
                                <th>Change</th>
                                <th>GRN Count</th>
                                <th>Total Quantity</th>
                              </tr>
                            </thead>
                            <tbody>
                              {vendor.priceHistory.map((history, historyIndex) => (
                                <tr key={`${vendor.vendorId}-${history.grnDate}-${historyIndex}`}>
                                  <td>{history.grnDate || 'N/A'}</td>
                                  <td>{history.invoiceDate || 'N/A'}</td>
                                  <td>
                                    <strong>{formatCurrency(history.rate)}</strong>
                                  </td>
                                  <td>
                                    {historyIndex < vendor.priceHistory.length - 1 && (
                                      <Badge bg={history.rate > vendor.priceHistory[historyIndex + 1].rate ? 'danger' : 'success'}>
                                        {calculatePriceChange(history.rate, vendor.priceHistory[historyIndex + 1].rate)}%
                                      </Badge>
                                    )}
                                  </td>
                                  <td>
                                    <Badge bg="info">{history.grnCount} GRN{history.grnCount > 1 ? 's' : ''}</Badge>
                                  </td>
                                  <td>
                                    <Badge bg="secondary">
                                      {history.totalQuantity || 0} {history.unit || ''}
                                    </Badge>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </Accordion.Body>
                      </Accordion.Item>
                    ))}
                  </Accordion>
                </div>
              ) : (
                <div className="text-center p-4">
                  <p className="text-muted">No price history found for this item</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default VendorPriceReport;